import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Video hero — faithful rewrite of legacy molecules/VideoHero (the /videos
// page). Gradient band with a heading beside an embedded YouTube video.
export const videoHeroSchema = z.object({
  heading: z.string().min(1),
  youtubeUrl: z.string().optional(),
});

type VideoHeroProps = z.infer<typeof videoHeroSchema>;

// Extract a YouTube video id from watch/share/embed URLs.
function youtubeId(url?: string): string | undefined {
  if (!url) return undefined;
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/);
  return m?.[1];
}

const Band = styled.section`
  overflow: hidden;
  border-radius: 0 0 80px 0;
  background: linear-gradient(93deg, #e8efff 0%, #fff 100%);
  padding: 0 2.4rem 4.8rem;
`;

const Inner = styled.div`
  max-width: 112rem;
  margin: 0 auto;
  padding-top: 8rem;
  display: grid;
  gap: 4rem;
  align-items: center;

  @media (min-width: 62em) {
    grid-template-columns: 1fr 2fr;
    padding-top: 12rem;
    padding-bottom: 4rem;
  }
`;

const Heading = styled.h1`
  margin: 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2rem;
  letter-spacing: -1px;

  @media (min-width: 48em) {
    font-size: 4rem;
    letter-spacing: -3px;
  }
`;

const Frame = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  border-radius: 2rem;
  overflow: hidden;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

function VideoHero({ heading, youtubeUrl }: VideoHeroProps) {
  const id = youtubeId(youtubeUrl);
  return (
    <Band>
      <Inner>
        <Heading>{heading}</Heading>
        {id ? (
          <Frame>
            <iframe
              src={`https://www.youtube.com/embed/${id}`}
              title={heading}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Frame>
        ) : null}
      </Inner>
    </Band>
  );
}

export const videoHeroBlock: BlockDefinition<VideoHeroProps> = {
  type: 'videoHero',
  schema: videoHeroSchema,
  Renderer: VideoHero,
};
